import {
  DataTable,
  type DataTableColumn,
  RowActionsMenu,
  StatusChip,
  type StatusTone,
} from '~/components/ui';
import { routes } from '~/app/routes';
import {
  ACKNOWLEDGE_ACTION,
  SERVICE_ACTIONS,
  SERVICE_HEALTH,
  SERVICE_STATUS,
  serviceActionReason,
  serviceChip,
  type ServiceRow,
} from '~/components/Constants/serviceConstants';

/** Edits open their own page instead of a confirm. */
const PAGE_ACTIONS: Record<string, (id: string) => string> = {
  Move: routes.serviceMove,
  Update: routes.serviceUpdate,
};

const Unset = () => <span className="text-gray-400 dark:text-gray-500">—</span>;

const Chip = ({ tones, value }: { tones: Record<string, StatusTone>; value?: string }) => {
  const { label, tone } = serviceChip(tones, value);
  return <StatusChip label={label} tone={tone} dot />;
};

interface ServicesTableProps {
  data: ServiceRow[];
  loading: boolean;
  refreshing: boolean;
  error?: { what: string; message?: string; onRetry?: () => void };
  /** A search or filter is narrowing the list, so an empty table means "no match". */
  filtered: boolean;
  selectedKeys: Set<string>;
  onToggleRow: (key: string) => void;
  onToggleAll: (checked: boolean) => void;
  onAction: (eventType: string, row: ServiceRow) => void;
}

export default function ServicesTable({
  data,
  loading,
  refreshing,
  error,
  filtered,
  selectedKeys,
  onToggleRow,
  onToggleAll,
  onAction,
}: ServicesTableProps) {
  const columns: DataTableColumn<ServiceRow>[] = [
    {
      id: 'name',
      header: 'Name',
      className: 'font-medium text-gray-800 dark:text-white/90',
      cell: (row) => row.name || <Unset />,
    },
    {
      id: 'health',
      header: 'Health',
      cell: (row) => <Chip tones={SERVICE_HEALTH} value={row.health} />,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => <Chip tones={SERVICE_STATUS} value={row.status} />,
    },
    {
      id: 'action',
      header: 'Action',
      className: 'capitalize',
      cell: (row) => row.action || <Unset />,
    },
    { id: 'actionStatus', header: 'Action status', cell: (row) => row.actionStatus || <Unset /> },
    {
      id: 'serviceName',
      header: 'Service name',
      className: 'capitalize',
      cell: (row) => row.serviceName || <Unset />,
    },
    { id: 'host', header: 'Host', cell: (row) => row.host || <Unset /> },
    { id: 'image', header: 'Image', cell: (row) => row.image || <Unset /> },
    {
      id: 'actions',
      header: 'Actions',
      cell: (row) => (
        <RowActionsMenu
          testId={`services-actions-${row._id}`}
          items={[...SERVICE_ACTIONS, ACKNOWLEDGE_ACTION].map((option) => {
            const reason = serviceActionReason(option.value, row);
            return {
              label: option.label,
              icon: option.icon,
              testId: option.value.toLowerCase(),
              destructive: option.destructive,
              disabled: reason !== undefined,
              disabledReason: reason,
              ...(PAGE_ACTIONS[option.value]
                ? { href: PAGE_ACTIONS[option.value](row._id) }
                : { onClick: () => onAction(option.value, row) }),
            };
          })}
        />
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={data}
      rowKey={(row) => row._id}
      rowLabel={(row) => row.name ?? row._id}
      error={error}
      loading={loading}
      refreshing={refreshing}
      emptyMessage={filtered ? 'No services match.' : 'No services yet.'}
      emptyDescription={
        filtered
          ? 'Clear the search or filters to see more services.'
          : 'Services appear here once containers are reporting.'
      }
      selectedKeys={selectedKeys}
      onToggleRow={onToggleRow}
      onToggleAll={onToggleAll}
      testId="services-table"
    />
  );
}
